import React, { useState } from 'react';
import { ChevronDownIcon } from '@revfluence/fresh-icons/solid/esm';
import { Button } from '@/shadcn/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/shadcn/components/ui/dropdown-menu';
import { cn } from '@/shadcn/lib/utils';
import { DEFAULT_VALUES, SHEET_EXTENSIONS_CONSTANTS } from '../constants';

export interface SheetTopBarActionItem {
  /** Unique identifier for the action */
  key: string;
  /** Display label for the action */
  label: string;
  /** Optional icon to display */
  icon?: React.ReactNode;
  /** Whether the action is disabled */
  disabled?: boolean;
  /** Optional data-dd-action-name for analytics */
  dataDdActionName?: string;
  /** Optional aria-label for accessibility */
  ariaLabel?: string;
  /** Optional sub-menu items */
  subItems?: SheetTopBarActionItem[];
  /** Optional custom content to render instead of standard menu item */
  customContent?: React.ReactNode;
  /** Whether this item should render as a separator */
  isSeparator?: boolean;
}

export interface SheetTopBarDropdownMenuProps {
  /** Array of action items to display */
  actions: SheetTopBarActionItem[];
  /** Callback when an action is clicked */
  onActionClick: (actionKey: string) => void;
  /** Optional additional CSS classes */
  className?: string;
  /** Optional button variant */
  variant?: 'default' | 'outline' | 'ghost';
  /** Optional button size */
  size?: 'default' | 'sm' | 'lg';
  /** Optional trigger button text */
  triggerText?: string;
  /** Optional custom trigger button */
  customTrigger?: React.ReactNode;
}

/**
 * SheetTopBarDropdownMenu
 * ----------------------
 * Displays a dropdown menu with actions in the sheet top bar.
 * Supports simple actions, sub-menus, separators, and custom content.
 *
 * Used across all sheet components for consistent action menu display.
 */
const SheetTopBarDropdownMenu: React.FC<SheetTopBarDropdownMenuProps> = ({
  actions,
  onActionClick,
  className,
  variant = DEFAULT_VALUES.ACTION_MENU_BUTTON_VARIANT,
  size = DEFAULT_VALUES.ACTION_MENU_BUTTON_SIZE,
  triggerText = DEFAULT_VALUES.ACTION_MENU_TRIGGER_TEXT,
  customTrigger,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const renderMenuItem = (action: SheetTopBarActionItem) => {
    // Render separator
    if (action.isSeparator) {
      return <DropdownMenuSeparator key={action.key} />;
    }

    // Render custom content
    if (action.customContent) {
      return (
        <div key={action.key} className="p-2">
          {action.customContent}
        </div>
      );
    }

    // Render sub-menu
    if (action.subItems && action.subItems.length > 0) {
      return (
        <DropdownMenuSub key={action.key}>
          <DropdownMenuSubTrigger
            data-dd-action-name={action.dataDdActionName}
            aria-label={action.ariaLabel}
            disabled={action.disabled}
            className="cursor-pointer flex items-center gap-2"
          >
            {action.icon}
            {action.label}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {action.subItems.map((subItem) => renderMenuItem(subItem))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      );
    }

    // Render standard menu item
    return (
      <DropdownMenuItem
        key={action.key}
        data-dd-action-name={action.dataDdActionName}
        aria-label={action.ariaLabel}
        disabled={action.disabled}
        className="cursor-pointer flex items-center gap-2"
        onClick={() => onActionClick(action.key)}
      >
        {action.icon}
        {action.label}
      </DropdownMenuItem>
    );
  };

  return (
    <div className={cn('flex items-center', className)}>
      <DropdownMenu onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          {customTrigger || (
            <Button
              variant={variant}
              size={size}
              className="h-8 px-3 flex items-center gap-2"
              aria-label="Actions"
            >
              {triggerText}
              <ChevronDownIcon
                className={cn(
                  SHEET_EXTENSIONS_CONSTANTS.ICON_SIZES.SMALL,
                  SHEET_EXTENSIONS_CONSTANTS.ANIMATION_DURATIONS.NORMAL,
                  isDropdownOpen ? 'rotate-180' : ''
                )}
              />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {actions.map(renderMenuItem)}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default SheetTopBarDropdownMenu;
